// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { HistoryType } from '@shared/utils/History/HistoryType';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { InstrumentSpecificType } from '@shared/utils/Instruments/InstrumentSpecificType';
import { QuoteSubscriptionItem } from './QuoteSubscriptionItem';
import { QuoteRequestListener } from './QuoteRequestListener';
import { EventEmitter } from 'events';
import { CONTINUOUS_CONTRACT_POSTFIX } from '@shared/utils/Enums/Constants';
export var QuoteCacheEventType;
(function (QuoteCacheEventType) {
    QuoteCacheEventType["OnSubscribeInstrument"] = "onSubscribeInstrument";
    QuoteCacheEventType["OnUnsubscribeInstrument"] = "onUnsubscribeInstrument";
})(QuoteCacheEventType || (QuoteCacheEventType = {}));
export class QuoteCache {
    constructor(dataCache) {
        this._eventEmitter = new EventEmitter();
        this.onRequestQuotesSubscription = new CustomEvent();
        this.onCancelQuotesSubscription = new CustomEvent();
        this.serverTimeZone = 0;
        this.subsDict = new Map(); // A dictionary quote of subscribers with keys as tradableId of instruments grouped by RouteId.
        this.SubsRoutesMap = new Map(); // key = Quote Route Id
        this.DataCache = dataCache;
        this._quoteRequestListener = new QuoteRequestListener(this);
    }
    subscribeEvent(event, callback) {
        this._eventEmitter.addListener(event, callback);
    }
    unsubscribeEvent(event, callback) {
        this._eventEmitter.removeListener(event, callback);
    }
    newQuoteMessage(msg, isInfoRoute = true) {
        const subsItem = this.getSubscriptionItemByMsg(msg); // Do not move this down: msg is modified below, so resolving the subscription item later can produce a different result.
        if (isInfoRoute &&
            !this._quoteRequestListener.isExistActiveSubscriber(Number(msg.Route), msg.InstrumentTradableID)) {
            this.sendRequestedQuoteToInstruments(msg);
            return;
        }
        const SubsItems = this.SubsRoutesMap.get(msg.Route.toString());
        if (!isNullOrUndefined(SubsItems)) {
            const Items = SubsItems.GetItems();
            for (let i = 0, len = Items.length; i < len; i++) {
                msg.Route = Items[i];
                this.newQuoteMessage(msg, false);
            }
        }
        if (isNullOrUndefined(subsItem)) {
            return;
        }
        if (subsItem.HasContinuousContractItem) {
            msg.TargetInstrumentName = subsItem.InstrumentContinuousContractItem.instrument.GetInteriorID();
            subsItem.InstrumentContinuousContractItem.newQuote(msg);
        }
        msg.TargetInstrumentName = subsItem.instrument.GetInteriorID();
        subsItem.newQuote(msg);
    }
    addListener(instrument, listenerObject, quoteType, isDataSource = undefined) {
        if (!this.DataCache.Loaded) {
            return;
        }
        const subsRoute = this.DataCache.Routes.getRouteByName(instrument.Route);
        if (!isNullOrUndefined(subsRoute) && subsRoute.QuoteRouteId !== subsRoute.RouteId) {
            const quoteSubsRoute = this.DataCache.Routes.getRouteById(subsRoute.QuoteRouteId);
            if (!isNullOrUndefined(quoteSubsRoute)) {
                if (!instrument.RoutesSupportedArray.includes(quoteSubsRoute.RouteId)) {
                    return;
                }
                let ItemSubs = this.SubsRoutesMap.get(quoteSubsRoute.RouteId.toString());
                if (isNullOrUndefined(ItemSubs)) {
                    ItemSubs = new SubscriptionMapItem(quoteSubsRoute.RouteId);
                    this.SubsRoutesMap.set(quoteSubsRoute.RouteId.toString(), ItemSubs);
                }
                ItemSubs.Add(subsRoute.RouteId);
            }
        }
        if (!isNullOrUndefined(subsRoute) && !subsRoute.CanSubscribe()) {
            return;
        }
        let delayQuotes = [];
        if (instrument.InstrumentSpecificType === InstrumentSpecificType.ContinuousContract) {
            const subIns = this.DataCache.getInstrument(instrument.InstrumentTradableID.toString(), instrument.Route);
            if (subIns) {
                this.addListener(subIns, listenerObject, quoteType);
                const subInsSubsItem = this.getSubscriptionItem(subIns);
                // if subIns already subscribed. Send all quotes
                if (!isNullOrUndefined(subInsSubsItem === null || subInsSubsItem === void 0 ? void 0 : subInsSubsItem.lastQuoteDict)) {
                    subInsSubsItem.lastQuoteDict.forEach((quote) => {
                        delayQuotes.push(quote);
                    });
                }
                delayQuotes = delayQuotes.concat(subIns.GeneratedInstrumentDayBarMessages());
            }
        }
        const subsItem = this.getSubscriptionItem(instrument);
        const listenersArray = subsItem.getListenerArray(quoteType);
        for (const q of delayQuotes) {
            subsItem.newQuote(q);
        }
        const isFirstSubscriber = listenersArray.length === 0;
        if (!listenersArray.includes(listenerObject)) {
            subsItem.addListener(listenerObject, quoteType);
        }
        if (isFirstSubscriber) {
            if (quoteType !== HistoryType.QUOTE_INSTRUMENT_DAY_BAR || isDataSource) {
                this.onRequestQuotesSubscription.Raise(instrument.InstrumentTradableID, instrument.Route, quoteType, false, isDataSource || false);
            }
            this._eventEmitter.emit(QuoteCacheEventType.OnSubscribeInstrument, instrument, quoteType);
        }
        if (quoteType !== HistoryType.QUOTE_INSTRUMENT_DAY_BAR && !isDataSource) {
            // подписка на IDBM сопровождает любую другую подписку (кроме подписки такого же типа или из дата сорс)
            this.addListener(instrument, listenerObject, HistoryType.QUOTE_INSTRUMENT_DAY_BAR, isDataSource);
        } // потому даже если меседж на подписку не нужен (его успел раньше отправить DataSource), надо все равно зарегистрировать слушателя как подписанного на IDBM (иначе при закрытии дата сорса произойдет отписка от IDBM)
    }
    removeListener(instrument, listenerObject, quoteType, isDataSource = false) {
        if (instrument.InstrumentSpecificType === InstrumentSpecificType.ContinuousContract) {
            const subIns = this.DataCache.getInstrument(instrument.InstrumentTradableID.toString(), instrument.Route);
            if (!isNullOrUndefined(subIns)) {
                this.removeListener(subIns, listenerObject, quoteType);
            }
            const subsItem = this.getSubscriptionItem(instrument, false);
            if (isNullOrUndefined(subsItem)) {
                return;
            }
            const oldListenersCount = subsItem.getListenerCount(quoteType);
            subsItem.removeListener(listenerObject, quoteType);
            const newListenersCount = subsItem.getListenerCount(quoteType);
            const wasRemovedListener = oldListenersCount !== newListenersCount;
            if (quoteType !== HistoryType.QUOTE_INSTRUMENT_DAY_BAR && !isDataSource && wasRemovedListener) {
                this.removeListener(instrument, listenerObject, HistoryType.QUOTE_INSTRUMENT_DAY_BAR, isDataSource);
            }
            return;
        }
        const subsItem = this.getSubscriptionItem(instrument, false);
        if (isNullOrUndefined(subsItem)) {
            return;
        }
        const oldListenersCount = subsItem.getListenerCount(quoteType);
        subsItem.removeListener(listenerObject, quoteType);
        const newListenersCount = subsItem.getListenerCount(quoteType);
        const wasRemovedListener = oldListenersCount !== newListenersCount;
        const isLastListener = wasRemovedListener && newListenersCount === 0;
        if (isLastListener) {
            if (quoteType !== HistoryType.QUOTE_INSTRUMENT_DAY_BAR || isDataSource) {
                this.onCancelQuotesSubscription.Raise(instrument.InstrumentTradableID, instrument.Route, quoteType, false, isDataSource || false);
            }
            this._eventEmitter.emit(QuoteCacheEventType.OnUnsubscribeInstrument, instrument, quoteType);
        }
        if (quoteType !== HistoryType.QUOTE_INSTRUMENT_DAY_BAR && !isDataSource && wasRemovedListener) {
            this.removeListener(instrument, listenerObject, HistoryType.QUOTE_INSTRUMENT_DAY_BAR, isDataSource);
        }
    }
    addQuoteRequestListener(instrument, listenerObject) {
        this._quoteRequestListener.Subscribe(instrument, listenerObject);
    }
    removeQuoteRequestListener(instrument, listenerObject) {
        this._quoteRequestListener.Unsubscribe(instrument, listenerObject);
    }
    reSubscribe(subscribe, instrumentList) {
        if (!isValidArray(instrumentList)) {
            return;
        }
        const allSubsItems = this.getAllSubscriptionItemArray();
        for (let i = 0; i < allSubsItems.length; i++) {
            const si = allSubsItems[i];
            const ins = si.instrument;
            if (!instrumentList.includes(ins)) {
                continue;
            }
            const ld = si.listenerDict;
            ld.forEach((arr, l) => {
                if (!isValidArray(arr)) {
                    return;
                }
                if (subscribe) {
                    this.onRequestQuotesSubscription.Raise(ins.InstrumentTradableID, ins.Route, l, true);
                }
                else {
                    this.onCancelQuotesSubscription.Raise(ins.InstrumentTradableID, ins.Route, l, true);
                }
            });
        }
    }
    getAllSubscriptionItemArray() {
        const result = Array.from(this.subsDict.values()).flatMap((routeDict) => Array.from(routeDict.values()));
        return result;
    }
    // TODO. Rename. Creates subs item as well if doesn't exist.
    getSubscriptionItem(instrument, createSubItem = true) {
        if (isNullOrUndefined(instrument))
            return null;
        // идентификаторы
        const route = instrument.Route;
        const tradableId = instrument.InstrumentTradableID;
        let subsItem = null;
        let routeItem = this.subsDict.get(route.toString());
        const isContinuous = instrument.InstrumentSpecificType === InstrumentSpecificType.ContinuousContract;
        // subsDict структура object[route] => object[tradableId]  =>  item
        if (isNullOrUndefined(routeItem)) {
            routeItem = new Map();
            this.subsDict.set(route.toString(), routeItem);
        }
        subsItem = routeItem.get(tradableId);
        if (isNullOrUndefined(subsItem) && createSubItem) {
            subsItem = new QuoteSubscriptionItem(instrument, this);
            routeItem.set(tradableId, subsItem);
        }
        if (isNullOrUndefined(subsItem)) {
            return null;
        }
        if (isContinuous) {
            if (!subsItem.HasContinuousContractItem) {
                subsItem.InstrumentContinuousContractItem = new QuoteSubscriptionItem(instrument, this);
                subsItem.HasContinuousContractItem = true;
            }
            subsItem = subsItem.InstrumentContinuousContractItem;
        }
        return subsItem;
    }
    getSubscriptionItemByMsg(msg) {
        if (isNullOrUndefined(msg)) {
            return null;
        }
        const routeItem = this.subsDict.get(msg.Route.toString());
        if (isNullOrUndefined(routeItem)) {
            return null;
        }
        const subsItem = routeItem.get(msg.InstrumentTradableID);
        if (isNullOrUndefined(subsItem)) {
            return null;
        }
        return subsItem;
    }
    resubscribe() {
        // TODO.
    }
    clear() {
        this.subsDict.forEach((routeDict) => {
            routeDict.forEach((quoteSubsItem) => {
                quoteSubsItem.dispose();
            });
        });
        this.subsDict.clear();
        this.SubsRoutesMap.clear();
        this._quoteRequestListener.Clear();
    }
    destroy() {
        this._quoteRequestListener.destroy();
        this.clear();
    }
    GetLevel2Cash(instrument) {
        const subsItem = this.getSubscriptionItem(instrument);
        if (!isNullOrUndefined(subsItem)) {
            return subsItem.lvl2CashItem;
        }
        return null;
    }
    sendRequestedQuoteToInstruments(msg) {
        const instruments = this.DataCache.getInstrumentsByTradableID(msg.InstrumentTradableID);
        for (let i = 0; i < instruments.length; i++) {
            const instrument = instruments[i];
            const isSent = this.sendQuoteToInstrument(instrument, msg);
            if (!isSent)
                continue;
            const isContinuous = instrument.InstrumentSpecificType === InstrumentSpecificType.ContinuousContract;
            if (isContinuous) {
                const continiousInstrument = this.DataCache.getInstrumentByTradable_ID(instrument.InstrumentTradableID + CONTINUOUS_CONTRACT_POSTFIX, instrument.Route);
                this.sendQuoteToInstrument(continiousInstrument, msg);
            }
        }
    }
    sendQuoteToInstrument(instrument, msg) {
        if (!instrument.RoutesSupportedArray.includes(msg.Route))
            return false;
        instrument.newQuote(msg);
        return true;
    }
}
class SubscriptionMapItem {
    constructor(RouteId) {
        this.RouteIdMap = new Map();
        this.RouteIdArray = [];
        this.RouteId = RouteId;
    }
    Add(routeId) {
        if (this.RouteIdMap.has(routeId)) {
            return;
        }
        this.RouteIdMap.set(routeId, true);
        this.RouteIdArray.push(routeId);
    }
    GetItems() {
        return this.RouteIdArray;
    }
}
